import { ConnectionsLoginResponse, LoginPayload, SSOLoginResponse, SecurePaymentsLoginResponse, SecurePaymentsSignOnResponse } from '@/types/loginResponse'
import { GoogleLoginResponse } from '@/types/googleLoginResponse'
import { SamlGetLoginRequest } from '@/types/samlLoginResponse'
import { useConfig } from '@/config/useConfig'
import { TokensPayload } from '@/types/tokensPayload'
import { MFAMethod } from '@/services/mfa'
import { getClient } from './client'
import { getQueryParameter } from '@/services/getQueryParameter'
import axios from 'axios'

const getSsoUrls = async (): Promise<{ basePath: string, apiPath: string }> => {
  const config = await useConfig()
  const SSO_BASE_PATH = config?.api?.url
  return { basePath: SSO_BASE_PATH, apiPath: `${SSO_BASE_PATH}${config?.api?.service_path}` }
}

export const getOidcParams = () => {
  const externalResponseType = getQueryParameter('response_type')
  const externalRedirectUri = getQueryParameter('redirect_uri')
  const externalClientId = getQueryParameter('client_id')
  const externalScope = getQueryParameter('scope')
  const externalState = getQueryParameter('state')
  return {
    shouldPassOidcParams: externalResponseType && externalRedirectUri && externalClientId && externalScope && externalState,
    oidcParams: { response_type: externalResponseType, redirect_uri: externalRedirectUri, client_id: externalClientId, scope: externalScope, state: externalState }
  }
}

export const redirectToRetrieveToken = async (body: TokensPayload): Promise<void> => {
  const { shouldPassOidcParams, oidcParams } = getOidcParams()

  try {
    const { apiPath } = await getSsoUrls()
    const form = document.createElement('form')
    document.body.appendChild(form)
    form.method = 'post'
    form.action = `${apiPath}/auth/retrieveToken`
    const keys = Object.keys(body) as Array<keyof typeof body>

    keys.forEach((name) => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = name
      const keyValue = body[name] as string
      input.value = keyValue
      form.appendChild(input)
    })

    if (shouldPassOidcParams) {
      const keys = Object.keys(oidcParams) as Array<keyof typeof oidcParams>
      keys.forEach((name) => {
        const input = document.createElement('input')
        input.type = 'hidden'
        input.name = name
        input.value = oidcParams[name] as string
        form.appendChild(input)
      })
    }

    form.submit()
    document.body.removeChild(form)
  } catch (error: any) {
    throw Error(formatError(error))
  }
}

const parseToken = (token: string) => {
  const parts = token.split('.')
  const tokenValue = JSON.parse(atob(parts[1]))
  return tokenValue
}

export const redirectToSamlProvider = async (body: { idpUrl: string, samlPayload: string }): Promise<void> => {
  try {
    const { idpUrl, samlPayload } = body

    const form = document.createElement('form')
    document.body.appendChild(form)
    form.method = 'post'
    form.action = idpUrl
    const samlRequestElement = document.createElement('input')
    samlRequestElement.type = 'hidden'
    samlRequestElement.value = samlPayload
    samlRequestElement.name = 'SAMLRequest'
    form.appendChild(samlRequestElement)
    document.body.appendChild(form)
    form.submit()
    document.body.removeChild(form)
  } catch (error: any) {
    throw Error(formatError(error))
  }
}

export const lmsUsernamePasswordLoginHttp = async (payload: LoginPayload): Promise<SSOLoginResponse> => {
  try {
    const client = getClient()
    const response = await client.post('/auth', payload)
    return response.data as SSOLoginResponse
  } catch (error: any) {
    throw Error(formatError(error))
  }
}

export const connectionsLoginHttp = async (serviceUrl: string, payload: LoginPayload): Promise<ConnectionsLoginResponse> => {
  try {
    const response = await axios.post(serviceUrl, payload)
    return response.data as ConnectionsLoginResponse
  } catch (error: any) {
    throw Error(formatError(error))
  }
}

export const securePaymentsLoginHttp = async (serviceUrl: string, payload: LoginPayload): Promise<SecurePaymentsLoginResponse> => {
  try {
    const response = await axios.post(serviceUrl, payload)
    return response.data as SecurePaymentsLoginResponse
  } catch (error: any) {
    throw Error(formatError(error))
  }
}

export const securePaymentsSignOnHttp = async (serviceUrl: string, token: string, secret: string): Promise<SecurePaymentsSignOnResponse> => {
  try {
    const headers = {
      secret,
      authorization: token
    }
    const response = await axios.post(serviceUrl, {}, { headers })
    return response.data as SecurePaymentsSignOnResponse
  } catch (error: any) {
    throw Error(formatError(error))
  }
}

export const totpLoginHttp = async (username: string, session: string, code: string, mfa_type?: MFAMethod): Promise<SSOLoginResponse> => {
  try {
    const body: Record<string,string> = { username, session, code }

    if (mfa_type) {
      body.mfa_type = mfa_type
    }

    const client = getClient()
    const response = await client.post('/auth/mfa', body)
    return response.data as SSOLoginResponse
  } catch (error: any) {
    throw Error(formatError(error))
  }
}

export const forgotPasswordHttp = async (username: string, provider: string): Promise<boolean> => {
  try {
    // The provider will be needed only during the time the migration feature is enable
    const client = getClient()
    await client.post('/auth/forgotPassword', { username, provider: provider || 'LMS' })
    return true
  } catch (error: any) {
    throw Error(formatError(error))
  }
}

export const resetPasswordHttp = async (username: string, newPassword: string, confirmationCode: string): Promise<boolean> => {
  try {
    const client = getClient()
    await client.post('/auth/resetPassword', { username, confirmation_code: confirmationCode, new_password: newPassword })
    return true
  } catch (error: any) {
    throw Error(formatError(error))
  }
}

export const resetPasswordHashedKeyHttp = async (hashedPassword: string, newPassword: string): Promise<SSOLoginResponse> => {
  try {
    const client = getClient()
    const response = await client.post('/auth/newPasswordHashedKey', { hashed_key: hashedPassword, new_password: newPassword })
    return response.data as SSOLoginResponse
  } catch (error: any) {
    throw Error(formatError(error))
  }
}

export const googleLogInHttp = async (googleToken: string): Promise<GoogleLoginResponse> => {
  try {
    const client = getClient()
    const response = await client.post('/sso/google', { google_token: googleToken })
    return response.data as GoogleLoginResponse
  } catch (error: any) {
    throw Error(formatError(error))
  }
}

export const newPasswordHttp = async (username: string, session: string, newPassword: string): Promise<SSOLoginResponse> => {
  try {
    const client = getClient()
    const response = await client.post('/auth/newPassword', { username, session, new_password: newPassword })
    return response.data as SSOLoginResponse
  } catch (error: any) {
    throw Error(formatError(error))
  }
}

export const samlLogInHttp = async (): Promise<SamlGetLoginRequest> => {
  try {
    const client = getClient()
    const response = await client.post('/sso/saml2')
    return response.data
  } catch (error: any) {
    throw Error(formatError(error))
  }
}

export const samlLogInHttpFromCustomPayloadProvider = async (providerUrl: string): Promise<{idpURL: string, samlPayload: string}> => {
  try {
    const response = await axios.get(providerUrl)
    return response.data?.d || response.data
  } catch (error: any) {
    throw Error(formatError(error))
  }
}

export const changePasswordHttp = async (oldPassword: string, accessToken: string, newPassword: string): Promise<SSOLoginResponse> => {
  try {
    const client = getClient()
    const response = await client.post('/auth/changePassword', { old_password: oldPassword, access_token: accessToken, new_password: newPassword })
    return response.data as SSOLoginResponse
  } catch (error: any) {
    throw Error(formatError(error))
  }
}

const formatError = (error: any) => {
  let errorMessage
  if (error.response) {
    errorMessage = error.response?.data?.message
      ? error.response?.data?.message
      : 'UNKNOWN_SERVER_ERROR'
  } else if (error.request) {
    // The request was made but no response was received
    // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
    // http.ClientRequest in node.js
    errorMessage = 'NO_RESPONSE_FROM_SERVER'
  } else {
    // Something happened in setting up the request that triggered an Error
    errorMessage = error.message
  }
  return errorMessage
}

export const httpLogins = {
  lms: lmsUsernamePasswordLoginHttp,
  connections: connectionsLoginHttp,
  securePayments: securePaymentsLoginHttp,
  securePaymentsSignOn: securePaymentsSignOnHttp
}
